/**
 * EX294 Ansible Exam Practice Page
 * Professional exam interface for RHCE/Ansible certification
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../hooks/useCurrency';
import { apiService } from '../services/api';
import {
  Play,
  Square,
  CheckCircle,
  Circle,
  Terminal,
  Server,
  Award,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  Loader2,
  BookOpen,
  Target,
  Lock,
  ArrowLeft,
  Info,
  XCircle,
  Settings,
  User,
  Key,
  FolderOpen,
  Flag,
  Check
} from 'lucide-react';

const ExamEX294 = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { isIndia } = useCurrency();
  const formatPrice = (inr, usd) => isIndia ? `₹${inr}` : `$${usd}`;
  const navigate = useNavigate();
  
  // State
  const [examSets, setExamSets] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [examDetail, setExamDetail] = useState(null);
  const [examStatus, setExamStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startingExamId, setStartingExamId] = useState(null);
  const [stoppingExam, setStoppingExam] = useState(false);
  const [error, setError] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [expandedHints, setExpandedHints] = useState({});
  const [expandedTask, setExpandedTask] = useState(null); // Accordion - only one task open
  const [completedTasks, setCompletedTasks] = useState({}); // Track completed tasks
  const [flaggedTasks, setFlaggedTasks] = useState({}); // Track flagged for revisit
  
  // Refs
  const heartbeatInterval = useRef(null);
  const iframeRef = useRef(null);

  // Check course access
  const checkAccess = useCallback(async () => {
    try {
      setCheckingAccess(true);
      const enrollment = await apiService.checkCourseEnrollment('EXAM_EX294');
      setHasAccess(enrollment.enrolled);
    } catch (err) {
      console.error('Access check failed:', err);
      setHasAccess(false);
    } finally {
      setCheckingAccess(false);
    }
  }, []);

  // Fetch exam sets
  const fetchExamSets = useCallback(async () => {
    try {
      setLoading(true);
      const sets = await apiService.getEX294ExamSets();
      setExamSets(sets);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch exam status
  const fetchExamStatus = useCallback(async () => {
    try {
      const status = await apiService.getEX294ExamStatus();
      setExamStatus(status);
      
      if (status.has_active_session && status.exam_id) {
        const detail = await apiService.getEX294ExamDetail(status.exam_id);
        setExamDetail(detail);
        setSelectedExam(status.exam_id);
      }
    } catch (err) {
      console.error('Failed to fetch exam status:', err);
    }
  }, []);

  // Start exam session
  const handleStartExam = async (examId) => {
    try {
      setStartingExamId(examId);
      setError(null);
      
      const result = await apiService.startEX294Exam(examId);
      
      if (result.success) {
        setExamStatus({
          has_active_session: true,
          exam_id: examId,
          control_node_ip: result.control_node_ip,
          terminal_url: result.terminal_url,
          tasks_completed: [],
          score: 0
        });
        
        const detail = await apiService.getEX294ExamDetail(examId);
        setExamDetail(detail);
        setSelectedExam(examId);
        
        startHeartbeat();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setStartingExamId(null);
    }
  };

  // Stop exam session
  const handleStopExam = async () => {
    setStoppingExam(true);
    try {
      await apiService.stopEX294Exam();
      setExamStatus(null);
      setExamDetail(null);
      setSelectedExam(null);
      stopHeartbeat();
    } catch (err) {
      setError(err.message);
    } finally {
      setStoppingExam(false);
    }
  };

  // Heartbeat management
  const startHeartbeat = () => {
    if (heartbeatInterval.current) return;
    
    heartbeatInterval.current = setInterval(async () => {
      try {
        await apiService.sendEX294ExamHeartbeat();
      } catch (err) {
        console.error('Heartbeat failed:', err);
      }
    }, 60000);
  };

  const stopHeartbeat = () => {
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
      heartbeatInterval.current = null;
    }
  };

  // Initial load
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      checkAccess();
      fetchExamSets();
      fetchExamStatus();
    }
  }, [authLoading, isAuthenticated, checkAccess, fetchExamSets, fetchExamStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopHeartbeat();
  }, []);

  // Start heartbeat when session is active
  useEffect(() => {
    if (examStatus?.has_active_session) {
      startHeartbeat();
    }
    return () => stopHeartbeat();
  }, [examStatus?.has_active_session]);

  // Redirect if not authenticated
  if (!authLoading && !isAuthenticated) {
    navigate('/login');
    return null;
  }

  // Loading state
  if (authLoading || loading || checkingAccess) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-80px)]">
        <Loader2 className="w-12 h-12 animate-spin text-orange-500" />
      </div>
    );
  }

  // No access - show purchase prompt
  if (!hasAccess) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700 p-8 text-center">
          {/* Ansible Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-28 h-28 bg-gradient-to-br from-gray-700 to-gray-900 rounded-2xl flex items-center justify-center shadow-2xl shadow-gray-500/30">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ansible/ansible-original.svg" alt="Ansible" className="w-20 h-20" />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-white mb-4">
            EX294 Ansible Exam Practice
          </h1>
          
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Master Red Hat EX294 (RHCE) certification with 3 complete practice exam sets.
            Real VM-based labs with 18 hands-on Ansible automation questions.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-gray-700/50 rounded-lg p-4">
              <Target className="w-8 h-8 text-orange-500 mx-auto mb-2" />
              <div className="text-white font-semibold">3 Exam Sets</div>
              <div className="text-gray-400 text-sm">18 Ansible Tasks</div>
            </div>
            <div className="bg-gray-700/50 rounded-lg p-4">
              <Terminal className="w-8 h-8 text-orange-500 mx-auto mb-2" />
              <div className="text-white font-semibold">Control Node</div>
              <div className="text-gray-400 text-sm">+ Managed Nodes</div>
            </div>
            <div className="bg-gray-700/50 rounded-lg p-4">
              <Award className="w-8 h-8 text-orange-500 mx-auto mb-2" />
              <div className="text-white font-semibold">RHCE Level</div>
              <div className="text-gray-400 text-sm">Real Exam Questions</div>
            </div>
          </div>
          
          <div className="flex flex-col items-center gap-4">
            <div className="text-4xl font-bold text-white">
              {formatPrice(499, 19)} <span className="text-lg text-gray-400 font-normal">only</span>
            </div>
            <button
              onClick={() => navigate('/payment/EXAM_EX294')}
              className="px-8 py-3 bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-600 hover:to-gray-800 text-white font-semibold rounded-lg transition-all shadow-lg shadow-gray-500/25 hover:shadow-xl flex items-center gap-2"
              data-testid="unlock-ex294-btn"
            >
              <Lock className="w-5 h-5" />
              Unlock EX294 Exam Practice
            </button>
            <button
              onClick={() => navigate('/courses?tab=exams')}
              className="text-gray-400 hover:text-white transition-colors flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // VM Starting - Show loading screen
  if (startingExamId) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <div className="text-center max-w-md mx-auto px-4">
          {/* Animated Ansible Logo */}
          <div className="relative mb-8">
            <div className="w-24 h-24 mx-auto bg-gradient-to-br from-gray-700 to-gray-900 rounded-2xl flex items-center justify-center shadow-2xl shadow-gray-500/30 animate-pulse">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ansible/ansible-original.svg" alt="Ansible" className="w-16 h-16" />
            </div>
            {/* Spinning ring */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-32 h-32 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin"></div>
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-3">
            Preparing Ansible Lab Environment
          </h2>
          
          <p className="text-gray-400 mb-6">
            Setting up your control node and managed nodes. This may take a few moments...
          </p>
          
          {/* Progress steps */}
          <div className="space-y-3 text-left bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-white" />
              </div>
              <span className="text-gray-300">Allocating resources</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center animate-pulse">
                <Loader2 className="w-4 h-4 text-white animate-spin" />
              </div>
              <span className="text-white">Starting control node</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-gray-600 rounded-full flex items-center justify-center">
                <Circle className="w-4 h-4 text-gray-400" />
              </div>
              <span className="text-gray-500">Connecting to managed nodes</span>
            </div>
          </div>
          
          <p className="text-xs text-gray-500 mt-4">
            Control Node + 5 Managed Nodes Environment
          </p>

          {/* Exam Overview Card */}
          {(() => {
            const examInfo = examSets.find(e => e.exam_id === startingExamId);
            if (!examInfo) return null;
            return (
              <div className="mt-6 text-left bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">While you wait — Exam Overview</p>
                <p className="text-white font-semibold mb-3">{examInfo.title}</p>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-gray-300">
                    <span className="w-5 h-5 bg-orange-500/20 rounded-full flex items-center justify-center text-orange-400 text-xs font-bold">{examInfo.task_count}</span>
                    <span>Tasks to complete</span>
                  </div>
                  {examInfo.max_score > 0 && (
                    <span className="text-gray-400 text-xs">{examInfo.max_score} pts total</span>
                  )}
                </div>
                {examInfo.description && (
                  <p className="text-gray-400 text-xs mt-3 leading-relaxed">{examInfo.description}</p>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    );
  }

  // Active exam session view
  if (examStatus?.has_active_session && examDetail) {
    const totalTasks = examDetail.tasks?.length || 0;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 -mt-16">
        {/* Enhanced Top Bar */}
        <div className="bg-gradient-to-r from-gray-800/90 via-gray-700/90 to-gray-800/90 backdrop-blur-sm border-b border-gray-600/50 px-4 py-3 shadow-lg">
          <div className="max-w-full mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 backdrop-blur rounded-xl flex items-center justify-center border border-white/20 shadow-lg">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ansible/ansible-original.svg" alt="Ansible" className="w-7 h-7" />
                </div>
                <div>
                  <span className="text-white font-bold text-lg">{examDetail.title}</span>
                  <div className="flex items-center gap-2 text-xs text-gray-300">
                    <Server className="w-3 h-3" />
                    <span>Control Node: {examStatus.control_node_ip}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-xl px-4 py-2 border border-white/20">
                <BookOpen className="w-4 h-4 text-white" />
                <span className="text-white font-semibold">{totalTasks} Tasks</span>
              </div>
              
              <button
                onClick={handleStopExam}
                disabled={stoppingExam}
                className="flex items-center gap-2 px-5 py-2 bg-white/10 hover:bg-white/20 backdrop-blur text-white rounded-xl transition-all border border-white/20 hover:border-white/40 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="end-exam-btn"
              >
                {stoppingExam ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
                {stoppingExam ? 'Ending Exam...' : 'End Exam'}
              </button>
            </div>
          </div>
        </div>

        {/* Main Content - Split View */}
        <div className="flex h-[calc(100vh-130px)]">
          {/* Left Panel - Questions */}
          <div className="w-[420px] bg-slate-900/80 backdrop-blur border-r border-slate-700/50 overflow-y-auto">
            <div className="p-5">
              {/* Section Header */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-gradient-to-br from-gray-600 to-gray-800 rounded-xl flex items-center justify-center shadow-lg shadow-gray-500/20">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Ansible Tasks</h2>
                  <p className="text-xs text-slate-400">Click on a task to expand</p>
                </div>
              </div>
              
              {/* Environment Info Card - Compact with all info */}
              <div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 rounded-xl p-4 mb-5 border border-gray-600/30 shadow-lg">
                <div className="flex items-center gap-2 text-sm text-gray-300 mb-3">
                  <div className="w-6 h-6 bg-orange-500/30 rounded-lg flex items-center justify-center">
                    <Info className="w-4 h-4 text-orange-400" />
                  </div>
                  <span className="font-semibold">Ansible Environment</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 text-xs bg-slate-800/50 rounded-lg px-2 py-1.5">
                    <User className="w-3 h-3 text-blue-400" />
                    <code className="text-blue-400 font-mono">su - admin</code>
                  </div>
                  <div className="flex items-center gap-2 text-xs bg-slate-800/50 rounded-lg px-2 py-1.5">
                    <Key className="w-3 h-3 text-pink-400" />
                    <code className="text-pink-400 font-mono">redhat</code>
                  </div>
                  <div className="flex items-center gap-2 text-xs bg-slate-800/50 rounded-lg px-2 py-1.5">
                    <FolderOpen className="w-3 h-3 text-yellow-400" />
                    <code className="text-yellow-400 font-mono text-[10px]">/home/admin/ansible</code>
                  </div>
                  <div className="flex items-center gap-2 text-xs bg-slate-800/50 rounded-lg px-2 py-1.5">
                    <Server className="w-3 h-3 text-emerald-400" />
                    <code className="text-emerald-400 font-mono">system1-5</code>
                  </div>
                  <div className="col-span-2 flex items-center gap-2 text-xs bg-slate-800/50 rounded-lg px-2 py-1.5">
                    <Key className="w-3 h-3 text-cyan-400" />
                    <span className="text-slate-400">SSH:</span>
                    <code className="text-cyan-400 font-mono text-[10px]">ansible_key</code>
                  </div>
                </div>
              </div>
              
              {/* Tasks List - Accordion Style with Checkbox & Flag */}
              <div className="space-y-2">
                {examDetail.tasks?.map((task, index) => {
                  const isExpanded = expandedTask === task.id;
                  const isHintExpanded = expandedHints[task.id] || false;
                  const isCompleted = completedTasks[task.id] || false;
                  const isFlagged = flaggedTasks[task.id] || false;
                  
                  return (
                    <div
                      key={task.id}
                      className={`rounded-xl border transition-all shadow-lg ${
                        isCompleted 
                          ? 'bg-emerald-900/30 border-emerald-500/50' 
                          : isFlagged
                            ? 'bg-amber-900/20 border-amber-500/50'
                            : isExpanded 
                              ? 'bg-slate-800/70 border-orange-500/50' 
                              : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600'
                      }`}
                      data-testid={`task-${task.id}`}
                    >
                      {/* Clickable Header */}
                      <div className="flex items-center">
                        {/* Checkbox */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const newCompleted = !completedTasks[task.id];
                            setCompletedTasks(prev => ({
                              ...prev,
                              [task.id]: newCompleted
                            }));
                            // Auto-remove flag when marking complete
                            if (newCompleted) {
                              setFlaggedTasks(prev => ({
                                ...prev,
                                [task.id]: false
                              }));
                            }
                          }}
                          className={`ml-3 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                            isCompleted 
                              ? 'bg-emerald-500 border-emerald-500' 
                              : 'border-slate-500 hover:border-emerald-400'
                          }`}
                          title={isCompleted ? 'Mark incomplete' : 'Mark complete'}
                        >
                          {isCompleted && <Check className="w-3 h-3 text-white" />}
                        </button>
                        
                        <button
                          onClick={() => setExpandedTask(isExpanded ? null : task.id)}
                          className="flex-1 p-3 flex items-center gap-2 text-left min-w-0"
                        >
                          <span className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-white text-sm font-bold ${
                            isCompleted
                              ? 'bg-gradient-to-br from-emerald-500 to-emerald-700'
                              : isExpanded 
                                ? 'bg-gradient-to-br from-orange-500 to-orange-700' 
                                : 'bg-gradient-to-br from-gray-600 to-gray-800'
                          }`}>
                            {index + 1}
                          </span>
                          <span className={`font-semibold text-sm truncate ${
                            isCompleted ? 'text-emerald-300 line-through' : 'text-white'
                          }`}>
                            {task.title}
                          </span>
                        </button>
                        
                        <button
                          onClick={() => setExpandedTask(isExpanded ? null : task.id)}
                          className="p-1.5 flex-shrink-0"
                        >
                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {/* Flag Button - Hidden if completed */}
                        {!isCompleted && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFlaggedTasks(prev => ({
                                ...prev,
                                [task.id]: !prev[task.id]
                              }));
                            }}
                            className={`mr-2 flex-shrink-0 p-1.5 rounded transition-all ${
                              isFlagged 
                                ? 'bg-amber-500/30 text-amber-400' 
                                : 'text-slate-500 hover:text-amber-400 hover:bg-slate-700'
                            }`}
                            title={isFlagged ? 'Remove flag' : 'Flag for review'}
                          >
                            <Flag className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      {/* Expandable Content */}
                      {isExpanded && (
                        <div className="px-3 pb-3 border-t border-slate-700/50">
                          {/* Question Description */}
                          <div className="mt-3 ml-10">
                            <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">
                              {task.description}
                            </p>
                          </div>
                          
                          {/* Hint Toggle Button */}
                          <div className="mt-3 ml-10">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedHints(prev => ({
                                  ...prev,
                                  [task.id]: !prev[task.id]
                                }));
                              }}
                              className="text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 hover:text-amber-300 px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all border border-amber-500/30"
                            >
                              <Info className="w-3 h-3" />
                              {isHintExpanded ? 'Hide Hint' : 'Show Hint'}
                            </button>
                            
                            {isHintExpanded && (
                              <div className="mt-3 p-3 bg-gradient-to-r from-amber-900/30 to-yellow-900/30 border border-amber-500/30 rounded-xl text-sm text-amber-200 whitespace-pre-line font-mono">
                                {task.hint}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Summary Bar */}
              <div className="mt-4 p-3 bg-slate-800/70 rounded-xl border border-slate-700/50">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5 text-emerald-400">
                      <Check className="w-4 h-4" />
                      {Object.values(completedTasks).filter(Boolean).length} Done
                    </span>
                    <span className="flex items-center gap-1.5 text-amber-400">
                      <Flag className="w-4 h-4" />
                      {Object.values(flaggedTasks).filter(Boolean).length} Flagged
                    </span>
                  </div>
                  <span className="text-slate-400">
                    {examDetail.tasks?.length - Object.values(completedTasks).filter(Boolean).length} Remaining
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Terminal */}
          <div className="flex-1 bg-slate-950 relative">
            {/* Terminal Header */}
            <div className="absolute top-0 left-0 right-0 bg-slate-900/90 backdrop-blur border-b border-slate-700/50 px-4 py-2 z-10">
              <div className="flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <span className="text-xs text-slate-400 ml-2 font-mono">admin@{examStatus.control_node_ip || 'control-node'}</span>
                <span className="text-xs text-slate-600 ml-auto">EX294 Ansible Control Node</span>
              </div>
            </div>
            
            {examStatus.terminal_url ? (
              <iframe
                ref={iframeRef}
                src={examStatus.terminal_url}
                title="Control Node Terminal"
                className="w-full h-full border-0 pt-10"
                allow="clipboard-read; clipboard-write"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-orange-500/20 rounded-2xl flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                  </div>
                  <p className="text-slate-400">Connecting to terminal...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Exam Sets Selection View
  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/courses?tab=exams')}
          className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-400" />
        </button>
        
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-gray-600 to-gray-800 rounded-xl flex items-center justify-center shadow-lg">
            <img
              src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ansible/ansible-original.svg"
              alt="Ansible"
              className="w-8 h-8"
            />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">EX294 Ansible Exam Practice</h1>
            <p className="text-gray-400">Red Hat RHCE Certification</p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-900/50 border border-red-700 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-200">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Info Banner */}
      <div className="mb-8 p-4 bg-gray-800/50 border border-gray-700 rounded-xl">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-orange-500/20 rounded-lg">
            <Info className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold mb-1">How It Works</h3>
            <p className="text-gray-400 text-sm">
              Each exam gives you a control node with 5 managed nodes pre-configured.
              Complete Ansible automation tasks including playbooks, roles, vaults, and system roles.
            </p>
            <div className="flex items-center gap-4 mt-3 text-sm">
              <div className="flex items-center gap-1 text-gray-300">
                <Terminal className="w-4 h-4 text-green-500" />
                <span>Control Node Terminal</span>
              </div>
              <div className="flex items-center gap-1 text-gray-300">
                <Server className="w-4 h-4 text-orange-500" />
                <span>5 Managed Nodes</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Exam Sets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {examSets.map((exam, index) => (
          <div
            key={exam.exam_id}
            className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700 overflow-hidden hover:border-orange-500/50 transition-all group"
            data-testid={`exam-set-${exam.exam_id}`}
          >
            {/* Header */}
            <div className="p-4 bg-gradient-to-r from-gray-700 to-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ansible/ansible-original.svg" alt="Ansible" className="w-6 h-6" />
                  <span className="text-white font-semibold">Exam Set {index + 1}</span>
                </div>
                <span className="px-2 py-1 bg-white/20 rounded text-xs text-white">
                  {exam.task_count} Tasks
                </span>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-4">
              <h3 className="text-white font-semibold mb-2">{exam.title}</h3>
              <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                {exam.description}
              </p>
              
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Award className="w-4 h-4 text-yellow-500" />
                  <span>{exam.max_score} Points</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Server className="w-4 h-4 text-orange-500" />
                  <span>Ansible</span>
                </div>
              </div>
              
              <button
                onClick={() => handleStartExam(exam.exam_id)}
                disabled={startingExamId === exam.exam_id}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 font-semibold rounded-lg transition-colors ${
                  startingExamId === exam.exam_id 
                    ? 'bg-gray-600 text-gray-300 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white'
                }`}
                data-testid={`start-exam-${exam.exam_id}`}
              >
                {startingExamId === exam.exam_id ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    Start Exam
                  </>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Tips Section */}
      <div className="mt-8 p-6 bg-gray-800/30 rounded-xl border border-gray-700">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-orange-500" />
          Ansible Exam Tips
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-400">
          <div className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 text-orange-500 mt-0.5" />
            <span>Working directory: <code className="text-yellow-400">/home/admin/ansible</code></span>
          </div>
          <div className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 text-orange-500 mt-0.5" />
            <span>Use <code className="text-green-400">ansible-doc</code> for module help</span>
          </div>
          <div className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 text-orange-500 mt-0.5" />
            <span>Check syntax with <code className="text-green-400">ansible-playbook --syntax-check</code></span>
          </div>
          <div className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 text-orange-500 mt-0.5" />
            <span>Use <code className="text-green-400">ansible-vault</code> for encrypted files</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamEX294;
